import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

dayjs.extend(duration);

type Props = {
  onEnd?: () => void;
  toUnixTimestamp: number;
};

export default function Countdown({ toUnixTimestamp, onEnd }: Props) {
  const { t } = useTranslation();

  const [countdown, setCountdown] = useState(
    getTimeDifferenceFromNow(toUnixTimestamp)
  );

  const updateCountdown = useCallback(() => {
    if (Date.now() > toUnixTimestamp * 1000) return onEnd?.();

    setCountdown(getTimeDifferenceFromNow(toUnixTimestamp));
  }, [toUnixTimestamp, setCountdown, onEnd]);

  useEffect(() => {
    updateCountdown();

    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [updateCountdown]);

  return (
    countdown && (
      <div className="flex gap-1">
        <CountdownUnit unit={t('day')} value={countdown.days} />
        <CountdownUnitSeperator />
        <CountdownUnit unit={t('hrs')} value={countdown.hours} />
        <CountdownUnitSeperator />
        <CountdownUnit unit={t('min')} value={countdown.minutes} />
        <CountdownUnitSeperator />
        <CountdownUnit unit={t('sec')} value={countdown.seconds} />
      </div>
    )
  );
}

function CountdownUnit({ unit, value }: { unit: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="flex justify-center font-bold text-grey-read">
        {value}
      </span>
      <span className="rounded-sm bg-concrete px-0.5 text-[10px]">{unit}</span>
    </div>
  );
}

function CountdownUnitSeperator() {
  return <span className="font-bold text-grey-read">:</span>;
}

function getTimeDifferenceFromNow(timestamp: number) {
  const startMs = Date.now();
  const endMs = timestamp * 1000;

  const difference = endMs > startMs ? endMs - startMs : 0;
  const duration = dayjs.duration(difference);

  if (duration.months() > 0 || duration.years() > 0) return;

  return {
    days: duration.days().toString().padStart(2, '0'),
    hours: duration.hours().toString().padStart(2, '0'),
    minutes: duration.minutes().toString().padStart(2, '0'),
    seconds: duration.seconds().toString().padStart(2, '0')
  };
}
